// CTurnTimer — 一回合倒數計時器（含畫面顯示）
// 從 game-scene.ts 抽出，封裝 Phaser.Time.TimerEvent + 秒數狀態 + 一個 Text 物件。
// 用途：玩家輪到時呼 start()；中途任何 phase 切換或回合結束都呼 stop()。
//
// 顯示位置由本檔上方常數設定（TIMER_X / TIMER_Y_FROM_BOTTOM），目前放在牌桌左下
// 剩餘牌數計數（CWallCounter）上方。如需移位只改這兩個常數。
//
// === 使用圖片 ===
// 無 asset — 純程式繪製：Phaser.GameObjects.Text（秒數）+ Rectangle（半透明襯底）

import type Phaser from 'phaser';
import { pop, animateTo } from './motion';

import { LOGICAL_HEIGHT } from '@config/game-config';
import { ZH_TW } from '@i18n/zh-tw';
import { FONT_STYLES } from './font-styles';
import { resolveTurnTimerDuration } from './turn-timer-config';
import { UIDepth } from './ui-depths';

// ---- 顯示位置（畫面左下，剩餘牌數計數上方） ----
const TIMER_PULSE_THRESHOLD_SEC = 3; // 最後三秒才輕跳
const TIMER_PULSE_MS = 180; // 短促提醒、不延長倒數
const TIMER_X = 195; // 與 CWallCounter 視覺中心約略對齊（wall counter 從 X=120 往右延伸約 150 寬）
const TIMER_Y_FROM_BOTTOM = 300; // 距畫面底部上推 — 約在剩餘牌數計數（Y_FROM_BOTTOM=170）上方 60px

// ---- 視覺樣式 ----
const TIMER_LOW_THRESHOLD_SEC = 5; // 剩 ≤N 秒時轉紅色警示
const TIMER_NORMAL_COLOR = '#ffffff'; // 正常色
const TIMER_LOW_COLOR = '#ff5546'; // 低秒數警示色
// 文字 style：在 FONT_STYLES.body 基礎上放大加粗，加強描邊；獨立於 body 以免改動波及全站
const TIMER_FONT_STYLE_OVERRIDE = {
  fontSize: '32px' as const,
  fontStyle: 'bold' as const,
  strokeThickness: 4,
} satisfies Partial<Phaser.Types.GameObjects.Text.TextStyle>;
// 文字後方襯底，避免在桌面紋理上被洗掉
const TIMER_BG_PAD_X = 14; // 襯底左右 padding（px）
const TIMER_BG_PAD_Y = 6; // 襯底上下 padding（px）
const TIMER_BG_COLOR = 0x000000; // 襯底底色
const TIMER_BG_ALPHA = 0.5; // 襯底透明度
const TIMER_DEPTH = UIDepth.TurnTimer; // 高於牌 sprite（0）、莊家 icon（52）、action 按鈕（60）

export type TTurnTimerConfig = {
  /** 倒數總秒數；null 表示不催促，省略時用 DEFAULT_TURN_TIMEOUT_SEC。 */
  readonly durationSec?: number | null;
  /** 倒數歸零後回呼；呼叫端負責後續動作（例如啟動 AI 代打）。 */
  readonly onTimeout: () => void;
};

/**
 * 一回合倒數計時器。
 * - start(): 重置秒數、顯示文字、開始 1Hz 倒數
 * - stop(): 移除 timer、隱藏文字
 */
export class CTurnTimer {
  private readonly scene: Phaser.Scene;
  private durationSec: number | null;
  private readonly onTimeout: () => void;
  private readonly text: Phaser.GameObjects.Text;
  private readonly bg: Phaser.GameObjects.Rectangle;

  private timerEvent: Phaser.Time.TimerEvent | null = null;
  private secondsLeft = 0;

  constructor(scene: Phaser.Scene, config: TTurnTimerConfig) {
    this.scene = scene;
    this.durationSec = resolveTurnTimerDuration(config.durationSec);
    this.onTimeout = config.onTimeout;

    const baseY = LOGICAL_HEIGHT - TIMER_Y_FROM_BOTTOM;
    // 襯底先畫（depth 略低於 text），尺寸於 refreshDisplay 依文字尺寸動態調整
    this.bg = scene.add
      .rectangle(TIMER_X, baseY, 10, 10, TIMER_BG_COLOR, TIMER_BG_ALPHA)
      .setOrigin(0.5, 0.5)
      .setDepth(TIMER_DEPTH - 1)
      .setVisible(false);
    this.text = scene.add
      .text(TIMER_X, baseY, '', {
        ...FONT_STYLES.body,
        ...TIMER_FONT_STYLE_OVERRIDE,
      })
      .setOrigin(0.5, 0.5)
      .setDepth(TIMER_DEPTH)
      .setVisible(false);
  }

  /** 重新開始倒數。若已在跑，先 stop；設定為不催促時 no-op。 */
  start(): void {
    this.stop();
    if (this.durationSec === null) return;
    this.secondsLeft = this.durationSec;
    this.refreshDisplay();
    this.text.setVisible(true);
    this.bg.setVisible(true);
    this.timerEvent = this.scene.time.addEvent({
      delay: 1000,
      repeat: this.durationSec - 1,
      callback: () => {
        this.secondsLeft -= 1;
        this.refreshDisplay();
        if (this.secondsLeft <= 0) {
          this.stop();
          this.onTimeout();
        }
      },
    });
  }

  /** 暫停目前倒數並保留秒數，供牌局暫停選單使用。 */
  pause(): void {
    if (this.timerEvent) this.timerEvent.paused = true;
  }

  /** 從暫停秒數繼續倒數。 */
  resume(): void {
    if (this.timerEvent) this.timerEvent.paused = false;
  }

  /** 套用新的玩家設定；呼叫端可在關閉設定畫面後依當前回合重新 start。 */
  setDurationSec(durationSec: number | null): void {
    if (this.durationSec === durationSec) return;
    this.stop();
    this.durationSec = durationSec;
  }

  /** 停止倒數並隱藏顯示。 */
  stop(): void {
    if (this.timerEvent) {
      this.timerEvent.remove();
      this.timerEvent = null;
    }
    animateTo(this.scene, this.text, { scaleX: 1, scaleY: 1 }, 0);
    this.text.setVisible(false);
    this.bg.setVisible(false);
  }

  /** 銷毀計時器持有的 scene 物件（text + bg）。scene shutdown 時呼叫，避免孤兒 GameObject。 */
  destroy(): void {
    this.stop();
    this.text.destroy();
    this.bg.destroy();
  }

  /** 依當前 secondsLeft 更新文字 / 顏色 / 襯底尺寸。 */
  private refreshDisplay(): void {
    this.text.setText(ZH_TW.game.hint.timeRemaining(this.secondsLeft));
    this.text.setColor(
      this.secondsLeft <= TIMER_LOW_THRESHOLD_SEC ? TIMER_LOW_COLOR : TIMER_NORMAL_COLOR
    );
    if (this.secondsLeft > 0 && this.secondsLeft <= TIMER_PULSE_THRESHOLD_SEC)
      pop(this.scene, this.text, TIMER_PULSE_MS);
    // 襯底依文字實際寬高 + padding 動態縮放，數字位數變動時自動跟著
    this.bg.setSize(this.text.width + TIMER_BG_PAD_X * 2, this.text.height + TIMER_BG_PAD_Y * 2);
  }
}
